import { CATEGORIES, CATEGORY_SLUGS } from '@sse/schema/categories';
import { CATEGORY_LABELS } from '@sse/ui';
import { useState } from 'react';
import { Card } from './ui';

/**
 * Uso del sito (docs/08-analytics.md): pagine viste per sezione, gli elementi più visti di ogni
 * sezione (comuni, categorie, artisti, bandi…), le funzioni usate e cosa si sceglie (filtri,
 * questionari). Le sezioni e le funzioni mai usate restano in elenco con zero.
 */
export interface Usage {
  sections: Record<string, number>;
  sectionItems: Record<string, Record<string, number>>;
  features: Record<string, number>;
  featureItems: Record<string, Record<string, number>>;
}

const nf = new Intl.NumberFormat('it-IT');

const SECTION_LABELS: Record<string, string> = {
  home: 'Home',
  event: 'Pagine evento',
  events: 'Elenco degli eventi',
  quick: 'Elenchi rapidi (oggi, weekend…)',
  category: 'Pagine categoria',
  categories: 'Elenco delle categorie',
  cities: 'Elenco dei comuni',
  city: 'Pagine comune',
  city_category: 'Comune + categoria',
  places: 'Elenco dei luoghi da vedere',
  place: 'Pagine luogo',
  beaches: 'Elenco delle spiagge',
  beach: 'Pagine spiaggia',
  calendar: 'Calendario per mese',
  collections: 'Elenco delle raccolte',
  collection: 'Raccolte',
  guests: 'Pagine per gli ospiti',
  artists: 'Elenco degli artisti',
  artist: 'Pagine artista',
  organizers: 'Organizzatori',
  grants: 'Elenco dei bandi',
  grants_quiz: 'Questionario dei bandi',
  grant: 'Pagine bando',
  grant_prepare: 'Preparazione della domanda',
  professionals: 'Elenco dei professionisti',
  professionals_quiz: 'Questionario dei professionisti',
  professional: 'Pagine professionista',
  'submit-event': 'Segnala un evento',
  saved: 'Preferiti',
  search: 'Ricerca',
  profile: 'Profilo',
  profile_stats: 'Statistiche dei propri eventi',
  survey: 'Sondaggi',
  about: 'Chi siamo',
  privacy: 'Privacy',
  cookie: 'Cookie',
  'booking-outcome': 'Esito della richiesta a un artista',
  'professional-request': 'Richiesta a un professionista',
  offline: 'Pagina offline',
  other: 'Altre pagine (anche indirizzi vecchi)',
};

/** Elementi più visti, per sezione. */
const ITEM_TITLES: Array<[string, string]> = [
  ['city', 'Comuni'],
  ['category', 'Categorie'],
  ['city_category', 'Comune + categoria'],
  ['place', 'Luoghi da vedere'],
  ['beach', 'Spiagge'],
  ['quick', 'Elenchi rapidi'],
  ['calendar', 'Mesi del calendario'],
  ['collection', 'Raccolte'],
  ['artist', 'Artisti'],
  ['grant', 'Bandi visti'],
  ['grant_prepare', 'Bandi: domanda in preparazione'],
  ['professional', 'Professionisti'],
  ['guests', 'Strutture (pagine per gli ospiti)'],
  ['survey', 'Sondaggi'],
];

const FEATURE_LABELS: Record<string, string> = {
  filter: 'Filtri dell’elenco',
  view: 'Passaggio tra elenco e mappa',
  side_map: 'Mappa accanto all’elenco',
  map_open: 'Aperture della mappa',
  search: 'Ricerche',
  directions: 'Indicazioni stradali',
  add_calendar: 'Aggiunte al calendario',
  share_event: 'Condivisioni',
  save_event: 'Salvataggi',
  click_source: 'Link alla fonte o al sito ufficiale',
  submit_event: 'Segnalazioni di eventi inviate',
  survey_open: 'Sondaggio aperto',
  survey_send: 'Sondaggio inviato',
  survey_dismiss: 'Barra del sondaggio chiusa',
  bandi_quiz: 'Questionario dei bandi completato',
  bandi_alert: 'Avviso sui nuovi bandi attivato',
  bando_follow: 'Bando seguito',
  bando_help: 'Aiuto chiesto su un bando',
  bando_draft: 'Bozza di domanda salvata',
  bando_check: 'Domanda fatta controllare',
  bando_request: 'Domanda di un bando inviata',
  artist_follow: 'Artista seguito',
  booking_quiz: 'Questionario degli artisti completato',
  booking_request: 'Richiesta a un artista',
  guest_place: 'Luogo aperto nella pagina per gli ospiti',
  guest_guide: 'Guida per chi viene da fuori',
  guest_button: 'Tasto della struttura toccato',
  guest_itinerary: 'Itinerario nella pagina per gli ospiti',
  guest_beach: 'Spiaggia nella pagina per gli ospiti',
};

const DETAIL_TITLES: Array<[string, string]> = [
  ['filter_period', 'Filtri: periodo'],
  ['filter_category', 'Filtri: categoria'],
  ['filter_city', 'Filtri: comune'],
  ['filter_near', 'Filtri: vicino a me (km)'],
  ['filter_kids', 'Filtri: per famiglie'],
  ['view_map', 'Elenco o mappa'],
  ['calendar_type', 'Calendario usato'],
  ['bandi_quiz_who', 'Questionario dei bandi: chi'],
  ['bandi_quiz_what', 'Questionario dei bandi: cosa'],
  ['bando_follow', 'Bandi seguiti'],
  ['bando_help', 'Aiuto chiesto sui bandi'],
  ['bando_check', 'Domande fatte controllare'],
  ['bando_request', 'Domande inviate'],
  ['booking_quiz_type', 'Questionario degli artisti: evento'],
  ['booking_quiz_kinds', 'Questionario degli artisti: cosa cercano'],
  ['artist_follow', 'Artisti seguiti'],
  ['booking_request', 'Richieste agli artisti'],
  ['guest_place', 'Luoghi aperti dagli ospiti'],
  ['guest_guide', 'Guida per gli ospiti: scheda'],
  ['guest_button', 'Tasti della struttura: tipo toccato'],
  ['guest_itinerary', 'Itinerari aperti dagli ospiti'],
  ['guest_itinerary_action', 'Itinerari: aperto, Google Maps, GPX'],
  ['guest_beach', 'Spiagge aperte dagli ospiti'],
  ['guest_beach_action', 'Spiagge: scheda aperta o indicazioni'],
  ['guest_beach_sea', 'Spiagge: che mare cercano'],
  ['guest_beach_with', 'Spiagge: con chi'],
  ['guest_beach_lido', 'Spiagge: lido o libera'],
  ['guest_beach_drive', 'Spiagge: quanto guidare (minuti)'],
  ['guest_beach_day', 'Spiagge: per domani'],
  ['survey_send', 'Sondaggi inviati'],
];

const CATEGORY_BY_SLUG: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [CATEGORY_SLUGS[c], CATEGORY_LABELS[c].plural]),
);
const PERIODS: Record<string, string> = {
  today: 'Oggi',
  tonight: 'Stasera',
  tomorrow: 'Domani',
  weekend: 'Weekend',
  'this-weekend': 'Questo weekend',
  'this-week': 'Questa settimana',
  'next-week': 'Prossima settimana',
  'next-30-days': 'Prossimi 30 giorni',
  upcoming: 'Tutti i prossimi',
  'for-families': 'Per famiglie',
  date: 'Date scelte a mano',
};
const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];
const MESI = [
  'gennaio',
  'febbraio',
  'marzo',
  'aprile',
  'maggio',
  'giugno',
  'luglio',
  'agosto',
  'settembre',
  'ottobre',
  'novembre',
  'dicembre',
];

/** Valori delle scelte nella scheda Spiagge della pagina per gli ospiti. */
const GUEST_BEACH_VALUES: Record<string, string> = {
  open: 'Scheda della spiaggia',
  directions: 'Indicazioni',
  sand: 'Sabbia',
  rock: 'Scogli e mare limpido',
  kids: 'Con bambini',
  dog: 'Con il cane',
  wheelchair: 'In carrozzina',
  clubs: 'Lido con servizi',
  free: 'Spiaggia libera',
  tomorrow: 'Domani',
  '15': 'Fino a 15 minuti',
  '30': 'Fino a 30 minuti',
  '45': 'Fino a 45 minuti',
};

/** "san-cesario-di-lecce" → "San Cesario di Lecce"; senza il codice finale dei bandi. */
const pretty = (slug: string) =>
  slug
    .replace(/-[0-9a-f]{8}$/, '')
    .split(/[-_]/)
    .map((w, i) => (i === 0 || w.length > 2 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

function label(key: string, value: string): string {
  if (key === 'category' || key === 'filter_category') return CATEGORY_BY_SLUG[value] ?? value;
  if (key === 'quick' || key === 'filter_period') return PERIODS[value] ?? value;
  if (key === 'calendar') {
    const [m, y] = [value.replace(/-\d{4}$/, ''), value.slice(-4)];
    const i = MONTHS.indexOf(m);
    return i >= 0 ? `${MESI[i]} ${y}` : value;
  }
  if (key === 'city_category') {
    const [city, cat] = value.split('/');
    return `${pretty(city ?? '')} · ${CATEGORY_BY_SLUG[cat ?? ''] ?? cat}`;
  }
  if (key.startsWith('guest_beach_')) return GUEST_BEACH_VALUES[value] ?? pretty(value);
  if (key === 'filter_kids') return value === '1' ? 'Attivato' : 'Tolto';
  if (key === 'view_map') return value === '1' ? 'Mappa' : 'Elenco';
  if (key === 'filter_near') return value === '0' ? 'Tolto' : `${value} km`;
  if (value === 'all') return 'Tolto (tutti)';
  return pretty(value);
}

const sortedEntries = (m: Record<string, number>) => Object.entries(m).sort((a, b) => b[1] - a[1]);

function TopList({ title, k, items }: { title: string; k: string; items: Record<string, number> }) {
  const [all, setAll] = useState(false);
  const rows = sortedEntries(items);
  const total = rows.reduce((n, [, v]) => n + v, 0);
  return (
    <Card title={title}>
      <ol className="flex flex-col text-sm">
        {(all ? rows : rows.slice(0, 8)).map(([v, n]) => (
          <li
            key={v}
            className="flex items-baseline justify-between gap-3 border-t border-surface-strong py-1.5 first:border-t-0"
          >
            <span className="min-w-0 break-words">{label(k, v)}</span>
            <span className="shrink-0 tabular-nums">
              {nf.format(n)}
              <span className="ml-2 inline-block w-9 text-right text-xs text-muted">
                {total ? `${Math.round((n / total) * 100)}%` : ''}
              </span>
            </span>
          </li>
        ))}
      </ol>
      {rows.length > 8 && (
        <button onClick={() => setAll(!all)} className="self-start text-xs underline">
          {all ? 'Mostra meno' : `Mostra tutti (${rows.length})`}
        </button>
      )}
    </Card>
  );
}

export function StatsUsage({ usage }: { usage: Usage }) {
  const sectionTotal = Object.values(usage.sections).reduce((a, b) => a + b, 0);
  const sections = Object.keys(SECTION_LABELS)
    .map((k) => [k, usage.sections[k] ?? 0] as const)
    .concat(
      Object.entries(usage.sections).filter(([k]) => !(k in SECTION_LABELS)) as Array<
        readonly [string, number]
      >,
    )
    .sort((a, b) => b[1] - a[1]);
  const features = Object.keys(FEATURE_LABELS)
    .map((k) => [k, usage.features[k] ?? 0] as const)
    .concat(
      Object.entries(usage.features).filter(([k]) => !(k in FEATURE_LABELS)) as Array<
        readonly [string, number]
      >,
    )
    .sort((a, b) => b[1] - a[1]);
  const lists = ITEM_TITLES.filter(([k]) => Object.keys(usage.sectionItems[k] ?? {}).length);
  const details = DETAIL_TITLES.filter(([k]) => Object.keys(usage.featureItems[k] ?? {}).length);

  return (
    <section aria-labelledby="uso-del-sito" className="flex flex-col gap-4">
      <h2 id="uso-del-sito" className="font-display text-h3">
        Uso del sito
      </h2>
      {sectionTotal === 0 ? (
        <p className="text-sm text-muted">Nessun dato nel periodo (si conta dal 4 ottobre 2026).</p>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <Card title="Sezioni del sito">
              <UsageTable
                head={['Sezione', 'Pagine viste', 'Quota']}
                rows={sections.map(([k, n]) => [
                  SECTION_LABELS[k] ?? k,
                  nf.format(n),
                  n ? `${Math.max(1, Math.round((n / sectionTotal) * 100))}%` : '—',
                ])}
                muted={sections.map(([, n]) => n === 0)}
              />
            </Card>
            <Card title="Funzioni usate">
              <UsageTable
                head={['Funzione', 'Volte']}
                rows={features.map(([k, n]) => [FEATURE_LABELS[k] ?? k, nf.format(n)])}
                muted={features.map(([, n]) => n === 0)}
              />
              <p className="text-xs text-muted">
                In grigio le sezioni e le funzioni mai usate nel periodo. I dettagli delle funzioni
                (cosa si sceglie nei filtri e nei questionari) si contano dal 15 ottobre 2026.
              </p>
            </Card>
          </div>
          {lists.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 md:items-start">
              {lists.map(([k, title]) => (
                <TopList key={k} title={title} k={k} items={usage.sectionItems[k]!} />
              ))}
            </div>
          )}
          {details.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 md:items-start">
              {details.map(([k, title]) => (
                <TopList key={k} title={title} k={k} items={usage.featureItems[k]!} />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function UsageTable({ head, rows, muted }: { head: string[]; rows: string[][]; muted: boolean[] }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-[0.04em] text-muted">
        <tr>
          {head.map((h, i) => (
            <th key={h} className={`py-1 font-medium ${i ? 'pl-3 text-right' : ''}`}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`border-t border-surface-strong ${muted[i] ? 'text-muted' : ''}`}>
            {r.map((c, j) => (
              <td key={j} className={`py-1.5 ${j ? 'pl-3 text-right tabular-nums' : 'pr-3'}`}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
